import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { uploadCompressedPhoto } from "@/lib/imageUpload";
import { useAuth } from "@/context/AuthContext";
import AuthImage from "@/components/AuthImage";
import { ArrowLeft, Camera, X, Plus, CheckCircle2 } from "lucide-react";
import { SOURCE_LABEL, SEVERITY_LABEL } from "@/lib/damagePdf";

const DAMAGE_TYPES = [
  "broken", "cracked", "scratched", "torn_packaging", "crushed_packaging",
  "wet_water", "stained", "missing_parts", "not_working", "opened_packaging",
  "bent", "chipped", "display_damage", "transit_damage", "other",
];
const DAMAGE_TYPE_LABEL = {
  broken: "Broken", cracked: "Cracked", scratched: "Scratched",
  torn_packaging: "Torn Packaging", crushed_packaging: "Crushed Packaging",
  wet_water: "Wet / Water Damage", stained: "Stained", missing_parts: "Missing Parts",
  not_working: "Not Working", opened_packaging: "Opened Packaging",
  bent: "Bent", chipped: "Chipped",
  display_damage: "Display Damage", transit_damage: "Transit Damage", other: "Other",
};
const CAUSES = [
  ["shipment", "Shipment / Transit"], ["supplier_packaging", "Supplier Packaging"],
  ["customer", "Customer Handling"], ["employee", "Employee Handling"],
  ["stockroom", "Stockroom Handling"], ["display_wear", "Display Wear"],
  ["cleaning", "Cleaning"], ["water_leak", "Water / Leak"],
  ["unknown", "Unknown"], ["other", "Other"],
];
const SELLABILITY = [
  ["full_price", "Yes – Full Price"],
  ["discount", "Yes – Discount Required"],
  ["unsellable", "No – Unsellable"],
  ["needs_review", "Needs GM Review"],
];

export default function DamageNew() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState({
    source_type: params.get("source") || "stockroom",
    inventory_receiving_id: params.get("receiving_id") || null,
    receiving_pallet_id: params.get("pallet_id") || null,
    display_check_id: params.get("display_check_id") || null,
    display_check_zone_id: params.get("display_zone_id") || null,
    fandom_id: params.get("fandom_id") || null,
    zone_id: params.get("zone_id") || null,
    product_name: "", sku: "", barcode: "",
    quantity: 1,
    damage_type: "broken",
    severity: "minor",
    cause: "unknown",
    description: "",
    unit_cost: "", retail_value: "",
    sellability_status: "needs_review",
    photos: [],
    moved_to_hold: false, damage_hold_location: "",
  });
  const [zones, setZones] = useState([]);
  const [fandoms, setFandoms] = useState([]);
  const [receivingCtx, setReceivingCtx] = useState(null);
  const [busyPhoto, setBusyPhoto] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/zones").then(({ data }) => setZones(data)).catch(() => {});
    api.get("/fandoms").then(({ data }) => setFandoms(data)).catch(() => {});
    if (f.inventory_receiving_id) {
      api.get(`/inventory_receivings/${f.inventory_receiving_id}`)
        .then(({ data }) => setReceivingCtx(data)).catch(() => {});
    }
  }, [f.inventory_receiving_id]);

  const set = (k, v) => setF({ ...f, [k]: v });

  const addPhoto = async (files) => {
    if (!files?.length) return;
    setBusyPhoto(true);
    try {
      const added = [];
      for (const file of Array.from(files)) {
        const { id, url } = await uploadCompressedPhoto(file);
        added.push({ id, file_id: id, url, photo_type: "product_damage" });
      }
      set("photos", [...(f.photos || []), ...added]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyPhoto(false); }
  };
  const removePhoto = (i) => set("photos", f.photos.filter((_, j) => j !== i));

  const canSave = f.source_type && f.quantity > 0 && f.damage_type && f.severity && f.cause;

  const submit = async () => {
    if (!canSave) return toast.error("Please fill required fields");
    setSaving(true);
    try {
      const payload = {
        ...f,
        quantity: Math.max(1, Number(f.quantity) || 1),
        unit_cost: f.unit_cost === "" ? null : Number(f.unit_cost),
        retail_value: f.retail_value === "" ? null : Number(f.retail_value),
        fandom_id: f.fandom_id || null, zone_id: f.zone_id || null,
        inventory_receiving_id: f.inventory_receiving_id || null,
        receiving_pallet_id: f.receiving_pallet_id || null,
        display_check_id: f.display_check_id || null,
        display_check_zone_id: f.display_check_zone_id || null,
      };
      const { data } = await api.post("/damages", payload);
      toast.success(`Damage recorded — ${data.reference_number}`);
      nav(`/damages/${data.id}`, { state: { justCreated: true } });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const pallet = f.receiving_pallet_id && receivingCtx
    ? (receivingCtx.pallets || []).find((p) => p.id === f.receiving_pallet_id) : null;

  return (
    <div className="space-y-6 max-w-3xl mx-auto pb-16" data-testid="damage-new-page">
      <button onClick={() => nav(-1)} className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back</button>
      <div>
        <div className="label-caps">New Damage Report</div>
        <h1 className="font-heading text-3xl">Report Damage</h1>
      </div>

      {/* Auto-context banners */}
      {receivingCtx && (
        <div className="hos-card p-4 border border-[color:var(--gold)]/40">
          <div className="label-caps mb-1">Linked to Receiving</div>
          <div className="text-sm">
            <b>{receivingCtx.reference_number}</b> · {receivingCtx.supplier_name || "—"}
            {pallet ? ` · Pallet ${pallet.pallet_number}` : ""}
            {receivingCtx.purchase_order_number ? ` · PO ${receivingCtx.purchase_order_number}` : ""}
          </div>
        </div>
      )}

      <div className="hos-card p-6 space-y-4">
        <div>
          <div className="label-caps">Where was the damage found?</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
            {Object.entries(SOURCE_LABEL).map(([k, l]) => (
              <button key={k} type="button" onClick={() => set("source_type", k)}
                      className={`p-3 rounded-md border-2 text-sm text-left ${f.source_type === k ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                      data-testid={`source-${k}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label-caps">Product Name</label>
            <input className="hos-input mt-1" value={f.product_name}
                   onChange={(e) => set("product_name", e.target.value)}
                   placeholder="e.g. Harry Potter Wand"
                   data-testid="product-name-input"/>
          </div>
          <div>
            <label className="label-caps">SKU / Barcode</label>
            <input className="hos-input mt-1" value={f.sku}
                   onChange={(e) => set("sku", e.target.value)}
                   placeholder="Scan or type"/>
          </div>
          <div>
            <label className="label-caps">Fandom</label>
            <select className="hos-input mt-1" value={f.fandom_id || ""} onChange={(e) => set("fandom_id", e.target.value || null)}>
              <option value="">— select fandom —</option>
              {fandoms.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps">Zone</label>
            <select className="hos-input mt-1" value={f.zone_id || ""} onChange={(e) => set("zone_id", e.target.value || null)}>
              <option value="">— select zone —</option>
              {zones.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label-caps">Quantity Damaged *</label>
            <input type="number" min="1" inputMode="numeric"
                   className="hos-input mt-1 text-2xl font-heading text-center" value={f.quantity}
                   onChange={(e) => set("quantity", Math.max(1, Number(e.target.value) || 1))}
                   data-testid="quantity-input"/>
          </div>
          <div>
            <label className="label-caps">Damage Type *</label>
            <select className="hos-input mt-1" value={f.damage_type} onChange={(e) => set("damage_type", e.target.value)} data-testid="damage-type-select">
              {DAMAGE_TYPES.map((k) => <option key={k} value={k}>{DAMAGE_TYPE_LABEL[k]}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps">Severity *</label>
            <select className="hos-input mt-1" value={f.severity} onChange={(e) => set("severity", e.target.value)} data-testid="severity-select">
              {Object.entries(SEVERITY_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label-caps">Cause</label>
            <select className="hos-input mt-1" value={f.cause} onChange={(e) => set("cause", e.target.value)}>
              {CAUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps">Sellability</label>
            <select className="hos-input mt-1" value={f.sellability_status} onChange={(e) => set("sellability_status", e.target.value)}>
              {SELLABILITY.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps">Unit Cost ($ — optional)</label>
            <input type="number" min="0" step="0.01" className="hos-input mt-1" value={f.unit_cost}
                   onChange={(e) => set("unit_cost", e.target.value)}/>
          </div>
          <div>
            <label className="label-caps">Retail Value ($ — optional)</label>
            <input type="number" min="0" step="0.01" className="hos-input mt-1" value={f.retail_value}
                   onChange={(e) => set("retail_value", e.target.value)}/>
          </div>
        </div>

        <div>
          <label className="label-caps">Description</label>
          <textarea rows={3} className="hos-input mt-1" value={f.description}
                    onChange={(e) => set("description", e.target.value)}
                    placeholder="Outer box crushed and product inside has a cracked corner."/>
        </div>

        <div>
          <label className="label-caps">Photos ({(f.photos || []).length})</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {(f.photos || []).map((p, i) => (
              <div key={i} className="relative">
                <AuthImage photo={p} className="w-20 h-20 object-cover rounded"/>
                <button onClick={() => removePhoto(i)} className="absolute -top-1 -right-1 bg-[color:var(--error)] rounded-full p-0.5"><X size={10}/></button>
              </div>
            ))}
            <label className="hos-btn-outline flex items-center gap-1 cursor-pointer text-sm">
              <Camera size={14}/> {busyPhoto ? "Uploading…" : "Add Photo"}
              <input type="file" accept="image/*" capture="environment" multiple className="hidden"
                     onChange={(e) => addPhoto(e.target.files)}/>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!f.moved_to_hold} onChange={(e) => set("moved_to_hold", e.target.checked)}/>
            Moved to Damage Hold Area
          </label>
          <input className="hos-input" placeholder="Damage bin / location (optional)"
                 value={f.damage_hold_location} onChange={(e) => set("damage_hold_location", e.target.value)}/>
        </div>

        <div className="pt-2">
          <button className="hos-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  onClick={submit} disabled={!canSave || saving} data-testid="submit-damage-btn">
            <CheckCircle2 size={16}/> {saving ? "Recording…" : "Record Damage"}
          </button>
        </div>
      </div>
    </div>
  );
}
